/**
 * web-client-app D4 — the website's addresses: `/`, `/events`, `/services`,
 * `/services/:id/:name?`, `/events/:id/:name?`. The same table the server's
 * dispatcher answers from (`matchWebPage` in @telify/shared); anything else is
 * the studio's not-found page. web-sign-in-checkout D7 adds `/checkout`, `/booked/:bookingId` and
 * `/account/bookings/:bookingId/pay`. web-account-pages D1 adds the signed-in client's
 * `/account/bookings`, `/account/bookings/:bookingId` and `/account/profile`, and a sent form's
 * `/form-request/:requestToken`, the studio's `/about`, and the app's Coming soon pages `/invite` and
 * `/waitlists`. shared-client-screens D5 adds a series' `/series/:seriesId/:name?`; web-account-pages
 * D11 a booking's Complete booking step, `/account/bookings/:bookingId/complete`. studio-shop D4 adds
 * the shop's `/shop/bag`, `/shop/checkout` and `/shop/orders/:orderId`; form-embed D3 a studio's
 * general form on its website, `/forms/:formKey`.
 *
 * web-studio-sites D3: on a studio's custom site the home, About and the services and events lists
 * are the site's own, so those routes are left out and the not-found page draws in their place, as
 * the server answers them.
 */
import { createBrowserRouter, createMemoryRouter, type RouteObject } from 'react-router-dom';

import { WebLayout } from './layout/WebLayout';
import { AboutPage } from './pages/AboutPage';
import { BookingPage } from './pages/account/BookingPage';
import { CompletePage } from './pages/account/CompletePage';
import { BookingsPage } from './pages/account/BookingsPage';
import { ProfilePage } from './pages/account/ProfilePage';
import { BookedPage } from './pages/BookedPage';
import { CheckoutPage } from './pages/CheckoutPage';
import { ComingSoonPage } from './pages/ComingSoonPage';
import { EventPage } from './pages/EventPage';
import { EventsPage } from './pages/EventsPage';
import { FormRequestWebPage } from './pages/FormRequestWebPage';
import { HomePage } from './pages/HomePage';
import { NotFoundPage } from './pages/NotFoundPage';
import { PayNowPage } from './pages/PayNowPage';
import { SeriesPage } from './pages/SeriesPage';
import { ShopBagPage } from './pages/shop/ShopBagPage';
import { ShopCheckoutPage } from './pages/shop/ShopCheckoutPage';
import { ShopOrderPage } from './pages/shop/ShopOrderPage';
import { SiteFormPage } from './pages/SiteFormPage';
import { PhoneTabsLayout } from './phone/PhoneTabsLayout';
import { ServicePage } from './pages/ServicePage';
import { ServicesPage } from './pages/ServicesPage';
import { readSite } from './site';

export const webRoutes: RouteObject[] = [
  {
    element: <WebLayout />,
    children: [
      // The app's tab shell on a phone (shared-client-screens D3): one frame across these pages.
      {
        element: <PhoneTabsLayout />,
        children: [
          { index: true, element: <HomePage /> },
          { path: 'invite', element: <ComingSoonPage screen="invite" /> },
          { path: 'waitlists', element: <ComingSoonPage screen="waitlists" /> },
          { path: 'account/bookings', element: <BookingsPage /> },
          { path: 'account/profile', element: <ProfilePage /> },
        ],
      },
      { path: 'events', element: <EventsPage /> },
      { path: 'about', element: <AboutPage /> },
      { path: 'services', element: <ServicesPage /> },
      { path: 'services/:serviceId/:nameSlug?', element: <ServicePage /> },
      { path: 'events/:eventId/:nameSlug?', element: <EventPage /> },
      // shared-client-screens D5: a series' own address.
      { path: 'series/:seriesId/:nameSlug?', element: <SeriesPage /> },
      { path: 'checkout', element: <CheckoutPage /> },
      { path: 'booked/:bookingId', element: <BookedPage /> },
      { path: 'account/bookings/:bookingId', element: <BookingPage /> },
      { path: 'account/bookings/:bookingId/pay', element: <PayNowPage /> },
      // web-account-pages D11: a booking the studio made that waits on the client.
      { path: 'account/bookings/:bookingId/complete', element: <CompletePage /> },
      { path: 'form-request/:requestToken', element: <FormRequestWebPage /> },
      // studio-shop D4: the shop's bag, checkout and order page (website only).
      { path: 'shop/bag', element: <ShopBagPage /> },
      { path: 'shop/checkout', element: <ShopCheckoutPage /> },
      { path: 'shop/orders/:orderId', element: <ShopOrderPage /> },
      // form-embed D3: a studio's general form on its website, by its website name.
      { path: 'forms/:formKey', element: <SiteFormPage /> },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
];

/** The standard website's own pages: the home (the index route), About and the two lists. */
const STANDARD_ONLY = new Set(['about', 'services', 'events']);

function withoutStandardOnly(routes: RouteObject[]): RouteObject[] {
  return routes
    .filter((r) => !r.index && !STANDARD_ONLY.has(r.path ?? ''))
    .map((r) => (r.children ? ({ ...r, children: withoutStandardOnly(r.children) } as RouteObject) : r));
}

/** This page's routes: a custom site's leave out the standard-only pages. */
function routesForThisPage(): RouteObject[] {
  return readSite() ? withoutStandardOnly(webRoutes) : webRoutes;
}

type WebRouter = ReturnType<typeof createBrowserRouter>;

export function createWebRouter(): WebRouter {
  return createBrowserRouter(routesForThisPage());
}

/** Test seam: the same routes in memory. */
export function createWebMemoryRouter(
  initialEntries: NonNullable<NonNullable<Parameters<typeof createMemoryRouter>[1]>['initialEntries']>,
  initialIndex?: number,
): WebRouter {
  return createMemoryRouter(routesForThisPage(), { initialEntries, initialIndex });
}
